/**
 * Login state machine (AUT-110).
 *
 * The admin login flow is a 2-step wizard living on a single URL
 * (`/auth/login`) — F5 mid-flow resets state, which is acceptable for v1
 * (≤20 admin users, internal). The state machine is a pure reducer so it
 * can be exercised in unit tests without a React tree.
 *
 * Transitions:
 *
 *   login ──(login_requires_totp)──▶ totp
 *   login ──(login_complete)──▶ (terminal — navigation handled by caller)
 *   totp  ──(totp_complete)──▶ (terminal — navigation handled by caller)
 *   totp  ──(back_to_login)──▶ login (also clears tempToken)
 *
 * `submit_started` / `submit_failed` are orthogonal — they flip the
 * `isSubmitting` / `errorKey` fields without changing `step`. Any action
 * other than `submit_failed` clears the previous `errorKey` so stale
 * banners don't carry across steps.
 */

export type LoginStep = 'login' | 'totp';

export type LoginState = {
  step: LoginStep;
  /**
   * Email entered at the login step — surfaced on the TOTP step header
   * so the operator sees *which account* the 2FA prompt is for.
   */
  email: string;
  tempToken: string | null;
  errorKey: string | null;
  isSubmitting: boolean;
};

export type LoginAction =
  | { type: 'submit_started' }
  | { type: 'submit_failed'; errorKey: string }
  | { type: 'login_requires_totp'; email: string; tempToken: string }
  | { type: 'login_complete'; email: string }
  | { type: 'totp_complete' }
  | { type: 'back_to_login' }
  | { type: 'clear_error' };

export const initialLoginState: LoginState = {
  step: 'login',
  email: '',
  tempToken: null,
  errorKey: null,
  isSubmitting: false,
};

export function loginReducer(state: LoginState, action: LoginAction): LoginState {
  switch (action.type) {
    case 'submit_started':
      return { ...state, isSubmitting: true, errorKey: null };
    case 'submit_failed':
      return { ...state, isSubmitting: false, errorKey: action.errorKey };
    case 'login_requires_totp':
      return {
        ...state,
        step: 'totp',
        email: action.email,
        tempToken: action.tempToken,
        errorKey: null,
        isSubmitting: false,
      };
    case 'login_complete':
      return { ...state, email: action.email, isSubmitting: false, errorKey: null };
    case 'totp_complete':
      return { ...state, isSubmitting: false, errorKey: null };
    case 'back_to_login':
      return {
        ...state,
        step: 'login',
        tempToken: null,
        errorKey: null,
        isSubmitting: false,
      };
    case 'clear_error':
      return { ...state, errorKey: null };
    default: {
      // Exhaustiveness check — TS errors here if a new action type is added
      // to LoginAction without a matching case branch.
      const _exhaustive: never = action;
      void _exhaustive;
      return state;
    }
  }
}
